import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  inject,
  OnDestroy,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject, takeUntil } from 'rxjs';
import { ProfileService } from 'src/app/_services/profile.service';
import { WidgetPositionService } from 'src/app/_services/review-services/widget-position.service';
import { ReviewService } from 'src/app/_services/review.service';
import { Widget } from 'src/app/models/widgetPosition';
import { BaseComponent } from '../base/base.component';

@Component({
  selector: 'app-review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.css']
})
export class ReviewComponent extends BaseComponent implements OnDestroy {
  isEdit: boolean = false;
  isComplete: boolean = false;
  hasReview: boolean = false;

  chosenUser: string;
  editWidget: Widget;
  components: Widget[] = [];
  hiddenComponents: Widget[] = [];

  selectedHiddenWidget: any = null;

  canViewSelect: any;
  userLogged: any;
  userLoggedRoleName: string;
  isAdmin: boolean;

  historicalReviewList: any[] = [];
  selectedReviewId: number;
  canBeConcluded: boolean;

  formattedText: string = '';

  constructor() {
    super();
  }

  private readonly title: Title = inject(Title);

  private readonly profileService: ProfileService = inject(ProfileService);
  private readonly widgetPositionService: WidgetPositionService = inject(
    WidgetPositionService
  );
  private readonly reviewService: ReviewService = inject(ReviewService);

  private readonly modal: NzModalService = inject(NzModalService);

  private readonly destroy$: Subject<void> = new Subject<void>();

  @ViewChild('reportModal', { static: true })
  reportModalTemplate!: TemplateRef<any>;

  async ngOnInit() {
    this.title.setTitle('Review | Sviluppo Personale');
    this.utilityService.setTitle('Review');

    this.userLogged = await this.profileService.getUserRoleAsync();
    this.userLoggedRoleName = this.userLogged.roles[0].name;

    const allowedRoles = ['admin', 'team_leader', 'project_manager'];
    this.canViewSelect = allowedRoles.includes(
      this.userLoggedRoleName.toLowerCase()
    );

    const rolesWithoutPersonalReview = ['admin'];
    this.isAdmin = rolesWithoutPersonalReview.includes(
      this.userLoggedRoleName.toLowerCase()
    );

    //Assegno a chosenUser l'id dell'utente loggato per gestione dei TL in review
    if (
      !!this.chosenUser == false &&
      !rolesWithoutPersonalReview.includes(
        this.userLoggedRoleName.toLowerCase()
      )
    ) {
      this.chosenUser = this.userLogged.id;
    }

    if (!this.isAdmin) {
      this.historicalReviewsSelect();
      this.getWidgets();
    }
  }

  public getUserSelect($event: any): void {
    this.chosenUser = $event;
    this.historicalReviewsSelect();
    this.getWidgets();
  }

  changeReviewHistorical(event: any) {
    if (this.selectedReviewId != event) {
      this.selectedReviewId = event;

      const selectedReview = this.historicalReviewList.find(
        r => r.id === event
      );

      if (selectedReview) {
        this.canBeConcluded =
          selectedReview.in_progress && selectedReview.can_be_concluded
            ? true
            : false;
      } else {
        this.canBeConcluded = false;
      }
    }
  }

  /**
   * Funzione per far visualizzare il bottone se il ruolo dell'utente corrente e' `admin`
   * oppure se l'utente è `pm | tl` e ha selezionato un utente dalla select
   */
  isButtonVisible(): boolean {
    if (this.userLogged === 'admin') return true;

    if (
      this.userLogged === 'pm' ||
      this.userLogged === 'tl' ||
      this.chosenUser
    ) {
      return true;
    }

    return false;
  }

  /**
   * Funzione per spostare e modificare tramite Drag and Drop le posizioni dei vari widget
   * @param event l'interno div con tutto il widget
   */
  onDrop(event: CdkDragDrop<any[]>) {
    const visible = this.components.filter(c => c.is_visible);

    const draggedItem = visible[event.previousIndex];
    const targetItem = visible[event.currentIndex];

    // Blocca lo spostamento se uno dei due è il widget "Dettagli utente"
    if (
      draggedItem.alias === 'Dettagli utente' ||
      targetItem.alias === 'Dettagli utente'
    ) {
      return;
    }

    const fromIndex = this.components.findIndex(c => c.id === draggedItem.id);
    const toIndex = this.components.findIndex(c => c.id === targetItem.id);

    if (fromIndex !== -1 && toIndex !== -1) {
      moveItemInArray(this.components, fromIndex, toIndex);
    }
  }

  get visibleComponents() {
    return this.components.filter(c => c.is_visible);
  }

  trackByComponentId(_index: number, item: any): any {
    return item.id;
  }

  /**
   * Funzione index per prendere tutti i widget
   */
  getWidgets() {
    this.widgetPositionService
      .getWidgets(this.chosenUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.components = res;
        },
        error: err => {
          console.error(err);
        }
      });
  }

  checkWidgetVisible(): Widget[] {
    return this.components.filter(widget => widget.is_visible === false);
  }

  /**
   * Nasconde un widget rimuovendolo dalla lista dei widget visibili e lo aggiunge alla lista dei widget nascosti.
   * @param index indice del widget all'interno dell'array `components` che si vuole nascondere
   */
  toggleVisibility(index: number) {
    const widget = this.components[index];

    widget.is_visible = false;
  }

  /**
   * Funzione che serve per nascondere i widget
   */
  editWidgetReview() {
    if (this.isEdit) {
      const payload = {
        widgets: this.components,
        chosen_user: this.chosenUser
      };

      this.widgetPositionService
        .editPositionWidget(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.getWidgets();
          },
          error: err => {
            console.error('Errore nel salvataggio:', err);
          }
        });
    }

    this.isEdit = !this.isEdit;
  }

  addWidget() {
    if (this.selectedHiddenWidget) {
      const component = this.selectedHiddenWidget;
      component.hidden = false;
      this.components.push(component); // Aggiungi ai visibili
      const index = this.hiddenComponents.indexOf(component);
      if (index > -1) {
        this.hiddenComponents.splice(index, 1); // Rimuovi dai nascosti
      }
      this.selectedHiddenWidget = null; // Reset della selezione
    }
  }

  /**
   * Funzione per salvare la review
   */
  saveReview = () => {
    this.reviewService
      .saveReview({ userId: this.chosenUser })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.responseToast('success', 'Review salvata con successo!');
          this.getWidgets();
          this.isComplete = false;
        },
        error: err => {
          this.responseToast('error', err.error.error);
        }
      });
  };

  /**
   * Serve per prendere i dati dal figlio e disabilitare il bottone in caso non avesse completato l'howIsGoing
   */
  onIsCompleteChange(value: boolean) {
    this.isComplete = value;
  }

  /**
   * Serve per prendere i dati dal figlio e far visualizzare la select delle review in caso ci siano delle review passate
   */
  handleLastReview(review: boolean) {
    this.hasReview = review;
  }

  widget(component: Widget) {
    component.is_visible = true;
  }

  /**
   * Ottengo le review passate dell'utente loggato per popolare la select seleziona review
   */
  historicalReviewsSelect() {
    this.reviewService.getReviewHistorical(this.chosenUser).subscribe(r => {
      this.historicalReviewList = r.result;

      // Imposta il primo elemento come selezionato di default
      if (this.historicalReviewList.length > 0) {
        this.selectedReviewId = this.historicalReviewList[0].id;
        this.canBeConcluded =
          this.historicalReviewList[0].in_progress &&
          this.historicalReviewList[0].can_be_concluded
            ? true
            : false;
      }
    });
  }

  getFormattedReportText() {
    if (!this.selectedReviewId) return;

    this.reviewService.getUserReport(this.selectedReviewId).subscribe({
      next: res => {
        this.formattedText = res.formatted_text;
      },
      error: err => {
        console.error('Errore nel recupero del report:', err);
      }
    });
  }

  openReportModal(): void {
    if (!this.selectedReviewId) return;

    this.reviewService.getUserReport(this.selectedReviewId).subscribe({
      next: res => {
        this.formattedText = res.formatted_text;

        this.modal.create({
          nzTitle: `Report`,
          nzContent: this.reportModalTemplate,
          nzFooter: null,
          nzWidth: 700
        });
      },
      error: () => {
        this.responseToast('error', 'Errore nel recupero del report');
      }
    });
  }

  copyFormattedText(): void {
    navigator.clipboard.writeText(this.formattedText).then(() => {
      this.responseToast('success', 'Testo copiato!');

      // Apri ChatGPT con nuova scheda (o altra destinazione)
      window.open('https://chat.openai.com/', '_blank');
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
